import {
  Button,
  ButtonList,
  DropdownButton,
  IconButton,
  Page,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { produce } from 'immer';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import partition from 'lodash/partition';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  CustomFieldAttribute,
  CustomFieldEntity,
  customFieldsApi,
} from 'apis/customFields';
import { appActions } from 'app/app.actions';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';

import { CustomAttributeModal } from './components/modal/customAttributeModal';
import { CustomAttributesTableRow } from './components/row/customAttributesTableRow';
import styles from './customAttributesTable.module.scss';

type CustomAttributesTableProps = {
  /** The entity. */
  entityId: CustomFieldEntity;
};

const filterOptions = {
  all: 'All attributes',
  active: 'Active attributes',
  inactive: 'Inactive attributes',
} as const;

const orderIndexStep = 2;

type FilterOption = keyof typeof filterOptions;

const entityTitle: Record<CustomFieldEntity, string> = {
  conversation: 'Conversation',
  contact: 'Contact',
};

export type FilterMode = keyof typeof filterOptions;

/** Represents the settings custom attributes table. */
export const CustomAttributesTable = ({
  entityId,
}: CustomAttributesTableProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const response = customFieldsApi.useReadAttributesQuery({
    entityId,
  });
  const asyncStatus = getAsyncStatusFromRtkQueryStates(response);
  const [filterMode, setFilterMode] = useState<FilterOption>('active');
  const recentlyToggledAttributes = useRef<Set<string>>(new Set([]));
  const allAttributesSorted = useMemo(() => {
    const dataAttributes = response.data?.data.attributes ?? [];
    return produce(dataAttributes, (draft) => {
      draft.sort((a, b) => a.order_index - b.order_index);
    });
  }, [response.data?.data.attributes]);
  const activeItemOrderIndexIsSequential = useMemo(() => {
    const active = allAttributesSorted.filter(
      (attribute) => !attribute.is_deactivated,
    );
    const activeOrderIndexes = active.map((attribute) => attribute.order_index);
    return activeOrderIndexes.every(
      (orderIndex, index) => orderIndex === index * orderIndexStep,
    );
  }, [allAttributesSorted]);
  const { attributes: allAttributes } = useMemo(() => {
    const dataAttributes = allAttributesSorted ?? [];
    return produce({ attributes: dataAttributes }, (draft) => {
      const [inactive, active] = partition(draft.attributes, 'is_deactivated');
      const sortByNameFn = (a: CustomFieldAttribute, b: CustomFieldAttribute) =>
        a.label.localeCompare(b.label);
      const sortByUpdatedAt = (
        a: CustomFieldAttribute,
        b: CustomFieldAttribute,
      ) => {
        const aUpdatedAt = new Date(a.updated_at).getTime();
        const bUpdatedAt = new Date(b.updated_at).getTime();
        return bUpdatedAt - aUpdatedAt;
      };
      const setOrderIndexFn = (attribute, i: number) => {
        const activeOffset = attribute.is_deactivated ? 20000000 : 0;
        attribute.order_index = i * orderIndexStep + activeOffset;
        return attribute;
      };

      draft.attributes = [
        ...active,
        ...inactive
          .sort(filterMode === 'inactive' ? sortByNameFn : sortByUpdatedAt)
          .map(setOrderIndexFn),
      ];
    });
  }, [allAttributesSorted, filterMode]);

  const filteredAttributes = useMemo(
    () =>
      [...allAttributes].filter((attribute) => {
        if (recentlyToggledAttributes.current.has(attribute.id)) {
          return true;
        }

        switch (filterMode) {
          case 'all':
            return true;
          case 'inactive':
            return attribute.is_deactivated === true;
          default:
            return attribute.is_deactivated === false;
        }
      }),
    [filterMode, allAttributes],
  );

  const handleAttributeActiveStateChange = useCallback(
    (attributeId: string) => {
      recentlyToggledAttributes.current.add(attributeId);
    },
    [],
  );

  /** Handles opening the creation modal. */
  const handleCreateCustomAttribute = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:customAttribute',
          arg: {
            entityId,
            mode: 'create',
            attributes: allAttributes,
          },
        },
      }),
    );

    ampli.settingsCustomAttributeAddNewAttributeClicked({ entityId });
  }, [allAttributes, dispatch, entityId]);

  /** Handles the event for when an attribute is moved. */
  const handleAttributeMove = async ({
    dragIndex,
    hoverIndex,
  }: DragMoveEvent) => {
    const newOrderIndex =
      (filteredAttributes[hoverIndex].order_index ?? 0) +
      (dragIndex < hoverIndex ? 1 : -1);

    // If the order_index is not sequential, we need to do a full recompute
    // otherwise we can just update the order_index of the dragged attribute
    const startIndex = activeItemOrderIndexIsSequential
      ? Math.min(dragIndex, hoverIndex)
      : 0;
    const endIndex = activeItemOrderIndexIsSequential
      ? Math.max(dragIndex, hoverIndex)
      : filteredAttributes.length - 1;

    const tempAttributes = produce(filteredAttributes, (draft) => {
      draft[dragIndex].order_index = newOrderIndex;
      draft.sort((a, b) => a.order_index - b.order_index);
    });

    let count = startIndex;
    for (let i = 0; i < tempAttributes.length; i++) {
      const originalAttribute = tempAttributes[i];
      const attributeId = originalAttribute.id;

      if (originalAttribute.is_deactivated || i < startIndex || i > endIndex) {
        // Skip deactivated attributes and attributes that are not in the changed range
        continue;
      }

      await dispatch(
        customFieldsApi.endpoints.updateAttribute.initiate({
          attributeId,
          entityId,
          patch: {
            before: originalAttribute,
            after: produce(originalAttribute, (draft) => {
              draft.order_index = count * orderIndexStep;
            }),
          },
        }),
      );

      ampli.settingsCustomAttributeReordered({
        attributeId,
        entityId,
        isFullRecompute: true,
      });

      // Keep track of the count for the next active attribute
      count++;
    }
  };

  const showFeedbackModal = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            team: entityId === 'contact' ? 'core' : 'conversations',
            title: 'Custom attributes feedback',
            extra: {
              entityId,
            },
          },
        },
      }),
    );
  }, [dispatch, entityId]);

  const handleFilterModeChange = useCallback((key: FilterOption) => {
    recentlyToggledAttributes.current.clear();
    setFilterMode(key);
  }, []);

  const controls = (
    <>
      <DropdownButton
        dropdown={
          <ButtonList>
            {Object.keys(filterOptions).map((key) => (
              <ButtonList.Item
                key={key}
                isSelected={filterMode === key}
                onClick={() => handleFilterModeChange(key as FilterOption)}
              >
                {filterOptions[key]}
              </ButtonList.Item>
            ))}
          </ButtonList>
        }
      >
        {filterOptions[filterMode]}
      </DropdownButton>

      <Button onClick={handleCreateCustomAttribute}>Add attribute</Button>
    </>
  );

  const noAttributesAtAll = !response.data?.data?.attributes?.length;
  const noVisibleAttributes = !filteredAttributes.length;

  const table = (
    <Table
      stickyHeader
      loading={asyncStatus === 'pending'}
      loadingFailed={asyncStatus === 'rejected'}
      emptyState={
        noVisibleAttributes && asyncStatus === 'fulfilled'
          ? {
              title: 'No attributes found',
              description: noAttributesAtAll
                ? 'Get started by adding your first attribute.'
                : 'Try changing your filters or search criteria.',
            }
          : undefined
      }
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name and description</Table.Th>

          <Table.Th width="15%">Identifier</Table.Th>

          <Table.Th width="0">Active</Table.Th>

          <Table.Th />
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {filteredAttributes.map((attribute, index) => (
          <CustomAttributesTableRow
            key={attribute.id}
            attribute={attribute}
            index={index}
            entityId={entityId}
            onMove={handleAttributeMove}
            onActiveStateChange={handleAttributeActiveStateChange}
          />
        ))}
      </Table.Tbody>
    </Table>
  );

  return (
    <>
      <Page.Section
        title={`${entityTitle[entityId]} attributes`}
        titleMeta={
          <>
            <Tooltip content="Help and documentation">
              <IconButton
                icon="question"
                variant="tertiary"
                onClick={() =>
                  elevioClient.openArticle(
                    articles.settings.customAttributes.documentation[entityId],
                  )
                }
              />
            </Tooltip>

            <span onClick={() => showFeedbackModal()}>
              <Text.Paragraph
                as="span"
                variant="4"
                className={styles.feedbackLink}
              >
                Send feedback
              </Text.Paragraph>
            </span>
          </>
        }
        controls={controls}
      >
        {table}
      </Page.Section>

      <CustomAttributeModal />
    </>
  );
};
